<template>
  <div class="settings-account" data-testid="settings-account">
    <!-- Email (read-only) -->
    <div class="sa-row">
      <div class="sa-row-info">
        <span class="sa-row-label">{{ STRINGS.settingsHub.account.email }}</span>
        <span class="sa-row-value" data-testid="settings-account-email">
          <LoadingDots v-if="emailLoading" data-testid="settings-account-email-loading" />
          <span v-else-if="emailError" class="sa-row-value--error">
            {{ STRINGS.settingsHub.account.emailLoadError }}
          </span>
          <template v-else>{{ email }}</template>
        </span>
      </div>
      <span
        v-if="auth.user.value && !auth.isEmailVerified.value"
        class="sa-badge-unverified"
        data-testid="settings-account-email-unverified"
      >
        {{ STRINGS.settingsHub.account.emailUnverified }}
      </span>
    </div>

    <!-- Password -->
    <div class="sa-row">
      <div class="sa-row-info">
        <span class="sa-row-label">{{ STRINGS.settingsHub.account.password }}</span>
        <span class="sa-row-value sa-row-value--masked">
          {{ STRINGS.settingsHub.account.passwordMasked }}
        </span>
      </div>
      <button
        v-if="mode === 'view'"
        class="sa-row-action"
        :aria-label="STRINGS.settingsHub.account.changePasswordAction"
        data-testid="settings-account-change-password-button"
        @click="mode = 'edit-password'"
      >
        {{ STRINGS.settingsHub.account.changeAction }}
      </button>
    </div>

    <!-- Inline password change form -->
    <form
      v-if="mode === 'edit-password'"
      class="sa-form"
      @submit.prevent="handlePasswordSubmit"
      novalidate
    >
      <div class="flex flex-col gap-1.5">
        <label for="settings-current-password" class="text-sm font-semibold text-arena-text-bright">
          {{ STRINGS.settingsHub.account.passwordForm.currentLabel }}
        </label>
        <InputText
          id="settings-current-password"
          v-model="currentPassword"
          type="password"
          class="sa-input min-h-12 touch-manipulation"
          :placeholder="STRINGS.settingsHub.account.passwordForm.currentPlaceholder"
          autocomplete="current-password"
          :aria-invalid="!!passwordError"
          data-testid="settings-account-current-password-input"
          @keyup.enter="handlePasswordSubmit"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <label for="settings-new-password" class="text-sm font-semibold text-arena-text-bright">
          {{ STRINGS.settingsHub.account.passwordForm.newLabel }}
        </label>
        <InputText
          id="settings-new-password"
          v-model="newPassword"
          type="password"
          class="sa-input min-h-12 touch-manipulation"
          :placeholder="STRINGS.settingsHub.account.passwordForm.newPlaceholder"
          autocomplete="new-password"
          :aria-invalid="!!passwordError"
          data-testid="settings-account-new-password-input"
          @keyup.enter="handlePasswordSubmit"
        />
        <p
          v-if="saveSuccess"
          class="text-xs text-energy-success flex items-center gap-1 m-0"
          role="status"
          aria-live="polite"
          data-testid="settings-account-password-success"
        >
          <AppIcon
            name="checkmark"
            :size="14"
            color="var(--color-energy-success)"
            aria-hidden="true"
          />
          {{ STRINGS.settingsHub.account.passwordForm.success }}
        </p>
        <p v-else class="text-xs text-arena-text-muted m-0">
          {{ STRINGS.settingsHub.account.passwordForm.hint }}
        </p>
      </div>

      <div class="flex flex-col gap-1.5">
        <label for="settings-confirm-password" class="text-sm font-semibold text-arena-text-bright">
          {{ STRINGS.settingsHub.account.passwordForm.confirmLabel }}
        </label>
        <InputText
          id="settings-confirm-password"
          v-model="confirmPassword"
          type="password"
          class="sa-input min-h-12 touch-manipulation"
          :placeholder="STRINGS.settingsHub.account.passwordForm.confirmPlaceholder"
          autocomplete="new-password"
          :aria-invalid="!!passwordError"
          data-testid="settings-account-confirm-password-input"
          @keyup.enter="handlePasswordSubmit"
        />
      </div>

      <!-- Error -->
      <p
        v-if="passwordError"
        class="text-sm text-error-500 text-center m-0"
        role="alert"
        aria-live="polite"
        data-testid="settings-account-password-error"
      >
        {{ passwordError }}
      </p>

      <div class="flex gap-3">
        <PrimeButton
          class="flex-1 min-h-12 touch-manipulation"
          :disabled="!canSavePassword"
          :aria-label="STRINGS.profile.buttons.save"
          data-testid="settings-account-save-password-button"
          @click="handlePasswordSubmit"
        >
          <template v-if="profileData.isLoading.value">
            {{ STRINGS.profile.buttons.save }}<LoadingDots />
          </template>
          <template v-else>
            {{ STRINGS.profile.buttons.save }}
          </template>
        </PrimeButton>
        <PrimeButton
          variant="secondary"
          class="flex-1 min-h-12 touch-manipulation"
          :aria-label="STRINGS.profile.buttons.cancel"
          data-testid="settings-account-cancel-password-button"
          @click="cancelPasswordEdit"
        >
          {{ STRINGS.profile.buttons.cancel }}
        </PrimeButton>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import InputText from 'primevue/inputtext';
import PrimeButton from '../ui/PrimeButton.vue';
import LoadingDots from '../ui/LoadingDots.vue';
import AppIcon from '../ui/icons/AppIcon.vue';
import { useAuth } from '@/composables/useAuth';
import { useProfile } from '@/composables/useProfile';
import { isAuthError } from '@/types/auth';
import { AUTH_CONFIG } from '@/config/constants';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';
import STRINGS from '@/constants/strings';

const auth = useAuth();
const profileData = useProfile();

const mode = ref<'view' | 'edit-password'>('view');

// Email is fetched lazily from GET /api/auth/account (it is not part of the
// resident auth identity object) when this tab mounts.
const email = ref('');
const emailLoading = ref(true);
const emailError = ref(false);

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const passwordError = ref('');
const saveSuccess = ref(false);

// Handle for the success-message auto-dismiss timer, captured so it can be
// cleared if the form is cancelled/reopened before it fires, and on unmount.
let successTimer: ReturnType<typeof setTimeout> | null = null;

function clearSuccessTimer(): void {
  if (successTimer !== null) {
    clearTimeout(successTimer);
    successTimer = null;
  }
}

onMounted(async () => {
  try {
    email.value = await profileData.fetchAccountEmail();
  } catch (err: unknown) {
    emailError.value = true;
    reportClientError(err, ErrorLocation.SettingsAccountEmailFetchFailed);
  } finally {
    emailLoading.value = false;
  }
});

onUnmounted(clearSuccessTimer);

const canSavePassword = computed(() => {
  return (
    currentPassword.value.length > 0 &&
    newPassword.value.length >= AUTH_CONFIG.PASSWORD_MIN_LENGTH &&
    newPassword.value === confirmPassword.value &&
    !profileData.isLoading.value &&
    !saveSuccess.value
  );
});

async function handlePasswordSubmit(): Promise<void> {
  if (!canSavePassword.value) {
    // Surface the most actionable client-side reason before hitting the server.
    if (
      newPassword.value.length > 0 &&
      confirmPassword.value.length > 0 &&
      newPassword.value !== confirmPassword.value
    ) {
      passwordError.value = STRINGS.settingsHub.account.errors.mismatch;
    }
    return;
  }

  passwordError.value = '';
  profileData.clearError();

  try {
    await profileData.changePassword(currentPassword.value, newPassword.value);
    currentPassword.value = '';
    newPassword.value = '';
    confirmPassword.value = '';
    saveSuccess.value = true;
    clearSuccessTimer();
    successTimer = setTimeout(() => {
      saveSuccess.value = false;
      mode.value = 'view';
      successTimer = null;
    }, 2000);
  } catch (err: unknown) {
    if (isAuthError(err)) {
      if (err.type === 'invalid_credentials') {
        passwordError.value = STRINGS.settingsHub.account.errors.wrongPassword;
      } else if (err.type === 'validation') {
        passwordError.value = STRINGS.settingsHub.account.errors.weakPassword;
      } else {
        passwordError.value = err.message;
      }
    } else {
      passwordError.value = STRINGS.auth.errors.network;
    }
  }
}

function cancelPasswordEdit(): void {
  clearSuccessTimer();
  mode.value = 'view';
  currentPassword.value = '';
  newPassword.value = '';
  confirmPassword.value = '';
  passwordError.value = '';
  saveSuccess.value = false;
  profileData.clearError();
}
</script>

<style scoped>
.settings-account {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.sa-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  background: var(--color-arena-void);
  border: 1px solid var(--color-arena-border);
  border-radius: 0.75rem;
}

.sa-row-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.sa-row-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--color-arena-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.sa-row-value {
  font-size: 0.875rem;
  color: var(--color-arena-text-bright);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sa-row-value--masked {
  letter-spacing: 0.1em;
  color: var(--color-arena-text-dim);
}

.sa-row-value--error {
  color: var(--color-arena-text-muted);
  font-style: italic;
}

.sa-badge-unverified {
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--color-energy-warning, #d9a441);
  background: rgba(217, 164, 65, 0.12);
  border: 1px solid rgba(217, 164, 65, 0.3);
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  flex-shrink: 0;
}

.sa-row-action {
  font-family: var(--font-sans);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-energy-primary);
  background: transparent;
  border: 1px solid rgba(201, 168, 76, 0.3);
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sa-row-action:hover {
  border-color: var(--color-energy-primary);
  background: rgba(201, 168, 76, 0.06);
}

.sa-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--color-arena-border);
  background: rgba(26, 22, 18, 0.8);
}

.sa-input {
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-arena-border);
  border-radius: var(--radius-input, 0.5rem);
  font-size: 1rem;
  transition: border-color 150ms ease-out;
  outline: none;
  background: rgba(26, 22, 18, 0.8);
  color: var(--color-arena-text-bright);
  width: 100%;
}

.sa-input:focus {
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 3px var(--color-primary-500);
}

.sa-input:focus-visible {
  outline: 3px solid var(--color-primary-500);
  outline-offset: 2px;
}

.sa-input::placeholder {
  color: var(--color-arena-text-muted);
}
</style>
